import React, { useCallback, useEffect, useMemo } from 'react';
import { Text, View, TouchableOpacity } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import {
  DocketDisplayOtherItem,
  DocketDetailsProps,
} from '../../../common/types';
import DocketItemStyles from '../DocketItem.styles';
import { useSession } from '../../../hooks/useSession';
import { NotifierDisplayStyle, PrinterProfile } from '@oolio-group/domain';
import { bulkSelectVar, selectedDocketVar } from '../../../state/apolloVars';

export default function OtherItemsDisplay({
  docketItem,
  onToggleDocketDetails,
  setDocketDetails,
  selectedDocket,
}: {
  docketItem: DocketDisplayOtherItem;
  onToggleDocketDetails: (props: DocketDetailsProps) => void;
  setDocketDetails: (a: DocketDetailsProps) => void;
  selectedDocket?: string;
}) {
  const { translate } = useTranslation();
  const styles = DocketItemStyles();
  const otherItems = docketItem.otherItems;
  const { session } = useSession();
  const isBulkMode = bulkSelectVar();
  const isSelected = Object.keys(selectedDocketVar())?.length > 0;

  useEffect(() => {
    if (selectedDocket === docketItem.docketId) {
      setDocketDetails({
        docketId: docketItem.docketId,
        items: docketItem.items,
        otherItems: otherItems,
        orderNumber: docketItem.orderNumber || '',
        status: docketItem.status,
        orderTypeId: docketItem.orderTypeId,
      });
    }
  }, [docketItem, selectedDocket, otherItems, setDocketDetails]);

  // Create the callback at the top level, before any conditional returns
  const handlePress = useCallback(() => {
    // Only execute the logic if otherItems exists
    if (otherItems?.length && otherItems[0]?.items?.length) {
      onToggleDocketDetails({
        docketId: docketItem.docketId,
        items: docketItem.items,
        otherItems: otherItems,
        orderNumber: docketItem.orderNumber || '',
        status: docketItem.status,
        orderTypeId: docketItem.orderTypeId,
      });
    }
  }, [docketItem, otherItems, onToggleDocketDetails]);

  const assignedProfiles = useMemo(
    () =>
      session?.kitchenDisplay?.printerProfiles?.map((x: PrinterProfile) => ({
        id: x.id,
        displayStyle: x.notifiers.displayStyle,
      })) || [],
    [session?.kitchenDisplay?.printerProfiles],
  );

  const printerProfile = useMemo(
    () => assignedProfiles.find(p => p.id === docketItem.printerProfileId),
    [assignedProfiles, docketItem.printerProfileId],
  );

  const isFullContentDisplay = useMemo(
    () =>
      printerProfile?.displayStyle === NotifierDisplayStyle.SHOW_FULL_CONTENT,
    [printerProfile],
  );

  const isSummaryDisplay = useMemo(
    () => printerProfile?.displayStyle === NotifierDisplayStyle.SHOW_SUMMARY,
    [printerProfile],
  );

  // Add safety checks
  if (!otherItems || !otherItems.length || !otherItems[0]?.items?.length) {
    return null;
  }

  const totalItemQuantity = otherItems.reduce((total, item) => {
    if (item.items) {
      return (
        total +
        item.items.reduce((itemTotal, subItem) => {
          return itemTotal + (subItem.quantity || 0);
        }, 0)
      );
    }
    return 0;
  }, 0);

  return (
    <TouchableOpacity
      onPress={handlePress}
      disabled={!isFullContentDisplay || isBulkMode || isSelected}
    >
      <View style={styles.otherItemsContainer}>
        <Text style={styles.otherItemsText}>
          {totalItemQuantity > 1
            ? `${translate('kitchenDisplay.otherItems')} (${totalItemQuantity})`
            : `${translate('kitchenDisplay.otherItem')} (${totalItemQuantity})`}
        </Text>
        {!isSummaryDisplay && <Text style={styles.arrowText}>{'>'}</Text>}
      </View>
    </TouchableOpacity>
  );
}
